import React, { useEffect, useState } from "react";
import { Autocomplete, TextField, CircularProgress, Box } from "@mui/material";
import {
  useLazyGetVaspDirectoryQuery,
  VaspDirectoryRow,
} from "src/store/features/api/notabene";

export interface VaspAutocompleteValue {
  vaspDid: string;
  vaspName: string;
  jurisdiction?: string;
}

export interface VaspAutocompleteProps {
  value: VaspAutocompleteValue | null;
  onChange: (next: VaspAutocompleteValue | null) => void;
}

/**
 * D3 Exchange branch — VASP autocomplete (preview / UX hint).
 *
 * Final VASP binding for the transfer happens later inside SafeConnect's
 * createWithdrawalAssist at withdrawal time — Notabene's SDK doesn't
 * expose a standalone vasp-picker widget, so this autocomplete is
 * informational at address-add time. Storing the picked VASP on the
 * address record lets WithdrawalAssist pre-fill the counterparty step
 * (and lets compliance audit the customer's declared destination).
 *
 * Backed by `GET /notabene/vasps?q=...` which proxies Notabene's
 * directory through the 24h `vaspResolutionCache`. Debounced 250ms.
 */
const VaspAutocomplete: React.FC<VaspAutocompleteProps> = ({ value, onChange }) => {
  const [inputValue, setInputValue] = useState(value?.vaspName ?? "");
  // The term the currently-held `data` was fetched for. We only trust `data`
  // when it matches the current input — otherwise rows from the previous query
  // would linger (client-side filtering is disabled below, so MUI no longer
  // hides them) and could be selected as a counterparty that doesn't match.
  const [searchedTerm, setSearchedTerm] = useState<string | null>(null);
  const [trigger, { data, isFetching, isSuccess, isError }] = useLazyGetVaspDirectoryQuery();

  const trimmed = inputValue.trim();

  useEffect(() => {
    if (trimmed.length < 2) return;
    const handle = setTimeout(() => {
      setSearchedTerm(trimmed);
      trigger(trimmed);
    }, 250);
    return () => clearTimeout(handle);
  }, [trimmed, trigger]);

  // Results are "current" only once a fetch for the exact current input has
  // settled successfully. During the 250ms debounce or while a request is in
  // flight this is false, so we show no stale rows and the empty-state reads
  // "Searching…" rather than a premature "No matching exchanges found".
  // `isSuccess` is required because RTK Query retains the last successful `data`
  // after an error — without it a failed fetch would fall back to the previous
  // query's rows (shown and selectable) instead of an empty state.
  const searchSettledForCurrent = !isFetching && searchedTerm !== null && searchedTerm === trimmed;
  const resultsAreCurrent = isSuccess && searchSettledForCurrent;
  const searchFailed = isError && searchSettledForCurrent;
  const options: VaspDirectoryRow[] = resultsAreCurrent && data ? data : [];

  return (
    <Box data-testid="vasp-autocomplete">
      <Autocomplete
        data-testid="vasp-autocomplete-input"
        options={options}
        loading={isFetching}
        value={value}
        onChange={(_event, next) => onChange(next)}
        inputValue={inputValue}
        onInputChange={(_event, next) => setInputValue(next)}
        getOptionLabel={(opt) => opt?.vaspName ?? ""}
        isOptionEqualToValue={(a, b) => a.vaspDid === b.vaspDid}
        // The backend (`GET /notabene/vasps`) already does the matching against
        // the Notabene directory. Disable MUI's default client-side filter:
        // it re-filters server rows by literal substring of the raw input, so
        // legitimate matches (e.g. Binance's legal entities "BAM Trading",
        // "Nest Services"; fuzzy matches; or a trailing space — default config
        // is trim:false) get silently dropped and the list renders empty even
        // though the backend returned rows. Pass results straight through.
        filterOptions={(opts) => opts}
        noOptionsText={
          trimmed.length < 2
            ? "Type at least 2 characters to search"
            : searchFailed
              ? "Couldn't load exchanges — please try again"
              : resultsAreCurrent
                ? "No matching exchanges found"
                : "Searching…"
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Exchange / VASP"
            placeholder="Search Notabene directory"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default VaspAutocomplete;
